import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Table, Card, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faEnvelope } from "@fortawesome/free-solid-svg-icons";

import { AppUserApi, ChatApi } from "../../../api";
import { AppUserType, PaginationType } from "../../../DTO";
import { chatListTypeSingle } from "../../../DTO/chat";
import { useAppNavigate, useAppPagination } from "../../../Router/hook";
import { useAppSelector } from "../../../redux-config/hooks";
import { selectUser } from "../../../redux-config/entities/user/selector";
import {
    CustomPagination,
    Loading,
    SearchFilters,
    UserActionsDropdown,
} from "../../../components";
import { CustomBreadCrumb } from "../../../components/customBreadCrumb";
import { formatUserId } from "../../../utils";
import "./style.scss";

const registerStateLabels: Record<string, string> = {
    preparing: "Preparing",
    pending: "Pending",
    accepted: "Accepted",
    finished: "Finished",
    rejected: "Rejected",
    personal_rejected: "Personal rejected",
    questionnaires: "Questionnaires",
    plan_purchased: "Plan purchased",
    set_password: "Set password",
};

const ONBOARDING_STATUSES = [
    { value: "referral", label: "Referral" },
    { value: "invitation", label: "Invitation" },
    { value: "app_install", label: "App Install" },
    { value: "account_setup", label: "Account Setup" },
    { value: "eligibility", label: "Eligibility" },
    { value: "questionnaires", label: "Questionnaires" },
    { value: "baseline_week", label: "Baseline Week (W1)" },
    { value: "program_started", label: "Program Started" },
] as const;

const getOnboardingStatusBadge = (status?: string | null) => {
    switch (status) {
        case "referral":
            return "app-user-badge app-user-badge-muted";
        case "invitation":
            return "app-user-badge app-user-badge-secondary";
        case "app_install":
            return "app-user-badge app-user-badge-warning";
        case "account_setup":
            return "app-user-badge app-user-badge-warning";
        case "eligibility":
            return "app-user-badge app-user-badge-info";
        case "questionnaires":
            return "app-user-badge app-user-badge-info";
        case "baseline_week":
            return "app-user-badge app-user-badge-primary";
        case "program_started":
            return "app-user-badge app-user-badge-success";
        default:
            return "app-user-badge app-user-badge-muted";
    }
};

const formatOnboardingStatus = (status?: string | null) => {
    if (!status) return "Not set";
    return (
        ONBOARDING_STATUSES.find((s) => s.value === status)?.label ??
        status.replace(/_/g, " ")
    );
};

const getRegisterStateBadge = (state?: string | null) => {
    switch (state) {
        case "finished":
            return "app-user-badge app-user-badge-success";
        case "accepted":
        case "questionnaires":
            return "app-user-badge app-user-badge-info";
        case "pending":
        case "preparing":
        case "set_password":
        case "plan_purchased":
            return "app-user-badge app-user-badge-warning";
        case "rejected":
        case "personal_rejected":
            return "app-user-badge app-user-badge-danger";
        default:
            return "app-user-badge app-user-badge-muted";
    }
};

const getStatusBadge = (status?: string | null) =>
    status === "active"
        ? "app-user-badge app-user-badge-success"
        : status === "released"
          ? "app-user-badge app-user-badge-released"
          : status === "inactive"
            ? "app-user-badge app-user-badge-muted"
          : "app-user-badge app-user-badge-warning";

const getRegistrationMethodBadge = (method?: string | null) =>
    method
        ? `app-user-login-method app-user-login-method-${method}`
        : "app-user-login-method app-user-login-method-unknown";

const formatRegistrationMethod = (method?: string | null) => {
    switch (method) {
        case "mobile":
            return "Mobile";
        case "email":
            return "Email";
        case "apple":
            return "Apple";
        case "mobile_apple":
            return "Mobile + Apple";
        default:
            return "Unknown";
    }
};

const getDiagnosisBadge = (diagnosis?: string | null) =>
    diagnosis
        ? `app-user-diagnosis app-user-diagnosis-${diagnosis.toLowerCase()}`
        : "app-user-diagnosis app-user-diagnosis-unknown";

const riskBandLabels = {
    green: "Green",
    amber: "Amber",
    red: "Red",
} as const;

const formatRegisterState = (state?: string | null) =>
    state
        ? registerStateLabels[state] || state.replace(/_/g, " ")
        : "Not started";

const getClinicName = (item: AppUserType) => {
    const clinic = item.clinic;
    const clinicName =
        clinic?.name ||
        clinic?.doctorName ||
        [clinic?.firstName, clinic?.lastName].filter(Boolean).join(" ");

    return clinicName || (item.clinicId ? `Clinic #${item.clinicId}` : "");
};

export const AppUsersList = () => {
    const [loading, setLoading] = useState(false);
    const [appUser, setAppUser] = useState<AppUserType[]>([]);
    const [waitingMessageUserIds, setWaitingMessageUserIds] = useState<
        Set<number>
    >(new Set());
    const [pagination, setPagination] =
        useState<PaginationType>(useAppPagination());
    const [email, setEmail] = useState("");
    const [query, setQuery] = useState("");
    const [phone, setPhone] = useState("");
    const [clinic, setClinic] = useState("");
    const [currentWeek, setCurrentWeek] = useState("all");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [registerState, setRegisterState] = useState("all");
    const [onboardingStatus, setOnboardingStatus] = useState("all");
    const [accountStatus, setAccountStatus] = useState("all");
    const [showRegistrationPath, setShowRegistrationPath] = useState(false);

    const user = useAppSelector(selectUser);
    const navigate = useAppNavigate();

    const fetchWaitingMessages = useCallback(async () => {
        try {
            const response = await ChatApi.FetchChatList(user);
            const conversations = (
                Array.isArray(response.data)
                    ? response.data
                    : Object.values(response.data ?? {})
            ) as chatListTypeSingle[];

            setWaitingMessageUserIds(
                new Set(
                    conversations
                        .filter((conversation) => conversation.client)
                        .map((conversation) => Number(conversation.user_id))
                        .filter(Number.isFinite),
                ),
            );
        } catch {
            // Keep the last known indicators if chat status refresh fails.
        }
    }, [user]);

    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "User list", href: "#" },
    ];

    const fetchAppUserList = useCallback(
        async (page: number) => {
            try {
                setLoading(true);

                const data = await AppUserApi.FetchUserAppList(user, page, {
                    query: query || undefined,
                    firstName: firstName || undefined,
                    lastName: lastName || undefined,
                    email: email || undefined,
                    phone: phone || undefined,
                    clinic: clinic || undefined,
                    currentWeek:
                        currentWeek !== "all" ? Number(currentWeek) : undefined,
                    status:
                        accountStatus !== "all"
                            ? (accountStatus as "active" | "inactive")
                            : undefined,
                    registerState:
                        registerState !== "all" ? registerState : undefined,
                    onboardingStatus:
                        onboardingStatus !== "all"
                            ? onboardingStatus
                            : undefined,
                });
                setAppUser(data.user);
                setPagination(data.pagination);
            } catch (err: any) {
                Swal.fire({ icon: "error", text: err.message });
            } finally {
                setLoading(false);
            }
        },
        [
            user,
            firstName,
            lastName,
            email,
            query,
            phone,
            clinic,
            currentWeek,
            accountStatus,
            registerState,
            onboardingStatus,
        ],
    );

    useEffect(() => {
        fetchAppUserList(pagination.current_page);
        fetchWaitingMessages();

        const messageRefreshTimer = window.setInterval(
            fetchWaitingMessages,
            30_000,
        );

        return () => window.clearInterval(messageRefreshTimer);
        // The list should load once on entry; filters are applied via Search.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchWaitingMessages]);

    const handleResetFilters = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setQuery("");
        setPhone("");
        setClinic("");
        setCurrentWeek("all");
        setAccountStatus("all");
        setRegisterState("all");
        setOnboardingStatus("all");
        navigate("/app-user?current_page=1");
        setLoading(true);
        AppUserApi.FetchUserAppList(user, 1)
            .then((data) => {
                setAppUser(data.user);
                setPagination(data.pagination);
            })
            .catch((err: any) =>
                Swal.fire({ icon: "error", text: err.message }),
            )
            .finally(() => setLoading(false));
    };

    return (
        <div className="my-4">
            <Helmet>
                <title>D2Type | User List</title>
            </Helmet>

            <CustomBreadCrumb breadCrumbList={crumbs} />
            <SearchFilters
                email={email}
                query={query}
                phone={phone}
                clinic={clinic}
                currentWeek={currentWeek}
                accountStatus={accountStatus}
                firstName={firstName}
                lastName={lastName}
                registerState={registerState}
                onboardingStatus={onboardingStatus}
                showRegistrationPath={showRegistrationPath}
                setQuery={setQuery}
                setPhone={setPhone}
                setClinic={setClinic}
                setCurrentWeek={setCurrentWeek}
                setAccountStatus={setAccountStatus}
                setEmail={setEmail}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setRegisterState={setRegisterState}
                setOnboardingStatus={setOnboardingStatus}
                setShowRegistrationPath={setShowRegistrationPath}
                onSearch={() => fetchAppUserList(1)}
                onReset={handleResetFilters}
            />

            {loading ? (
                <Loading />
            ) : (
                <Card className="shadow-sm app-users-card">
                    <Card.Body>
                        <div className="app-users-pagination-top">
                            <CustomPagination
                                total={pagination.total}
                                perPage={pagination.per_page}
                                currentPage={pagination.current_page}
                                changePage={(page: number) => {
                                    navigate(`/app-user?current_page=${page}`);
                                    fetchAppUserList(page);
                                }}
                            />
                        </div>
                        <Table
                            hover
                            responsive
                            className={`align-middle app-users-table ${
                                showRegistrationPath
                                    ? "show-registration-path"
                                    : ""
                            }`}
                        >
                            <thead>
                                <tr>
                                    <th>User ID</th>
                                    <th>User</th>
                                    <th>Clinic Referred</th>
                                    <th>Diagnosis</th>
                                    <th>Risk Band</th>
                                    <th>Onboarding Status</th>
                                    <th>Current Week</th>
                                    <th className="registration-path-column">
                                        Registration Path
                                    </th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {appUser.length > 0 ? (
                                    appUser.map((item, idx) => {
                                        const displayUserId =
                                            item.userId || item.id;
                                        const publicUserId = item.userId
                                            ? item.displayUserId ||
                                              formatUserId(
                                                  item.userId,
                                                  item.createdAt,
                                              )
                                            : "Pending";
                                        const userName = [
                                            item.firstName,
                                            item.lastName,
                                        ]
                                            .filter(Boolean)
                                            .join(" ")
                                            .trim();
                                        const displayName =
                                            userName ||
                                            item.email ||
                                            "Unnamed user";
                                        const clinicName = getClinicName(item);
                                        const isClinicReferral =
                                            Boolean(clinicName) ||
                                            item.signupSource === "clinic";
                                        const canToggleStatus =
                                            !item.isLoginReleased &&
                                            (item.status === "active" ||
                                                item.status === "inactive");
                                        const hasWaitingMessage =
                                            waitingMessageUserIds.has(
                                                Number(displayUserId),
                                            );

                                        return (
                                            <tr
                                                key={item.id}
                                                className={
                                                    hasWaitingMessage
                                                        ? "app-user-has-new-message"
                                                        : undefined
                                                }
                                            >
                                                <td className="app-users-index">
                                                    {publicUserId}
                                                </td>
                                                <td className="app-users-user-cell">
                                                    <div className="app-users-name-row">
                                                        <span className="app-users-name">
                                                            {displayName}
                                                        </span>
                                                        {item.isLoginReleased && (
                                                            <span className="app-user-badge app-user-badge-released">
                                                                Released
                                                            </span>
                                                        )}
                                                    </div>
                                                    {hasWaitingMessage && (
                                                        <Link
                                                            to={`/Message/${displayUserId}`}
                                                            className="app-user-new-message"
                                                            title="Open conversation and reply"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faEnvelope}
                                                            />
                                                            <span>
                                                                New message —
                                                                Reply
                                                            </span>
                                                        </Link>
                                                    )}
                                                    {(item.openTicketCount ??
                                                        0) > 0 && (
                                                        <Link
                                                            to={`/tickets?user_id=${displayUserId}`}
                                                            title="Open review ticket"
                                                            style={{
                                                                textDecoration:
                                                                    "none",
                                                            }}
                                                        >
                                                            <span className="app-user-badge app-user-badge-danger mt-1 d-inline-block">
                                                                🎫{" "}
                                                                {
                                                                    item.openTicketCount
                                                                }{" "}
                                                                review ticket
                                                                {(item.openTicketCount ??
                                                                    0) > 1
                                                                    ? "s"
                                                                    : ""}
                                                            </span>
                                                        </Link>
                                                    )}
                                                    <div className="app-users-contact">
                                                        <span>
                                                            {item.email ||
                                                                item.releasedEmail ||
                                                                "No email"}
                                                            {!item.email &&
                                                                item.releasedEmail &&
                                                                " (released)"}
                                                        </span>
                                                        {(item.phone ||
                                                            item.releasedPhone) && (
                                                            <span>
                                                                {item.phone ||
                                                                    item.releasedPhone}
                                                                {!item.phone &&
                                                                    item.releasedPhone &&
                                                                    " (released)"}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {isClinicReferral ? (
                                                        <div className="app-users-clinic-cell">
                                                            <span className="app-user-badge app-user-badge-info">
                                                                Clinic
                                                            </span>
                                                            <strong>
                                                                {clinicName ||
                                                                    "Clinic referral"}
                                                            </strong>
                                                        </div>
                                                    ) : (
                                                        <span className="app-users-muted-text">
                                                            Direct
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={getDiagnosisBadge(
                                                            item.diagnosis,
                                                        )}
                                                    >
                                                        {item.diagnosis ||
                                                            "Not set"}
                                                    </span>
                                                </td>
                                                <td>
                                                    {item.riskBand ? (
                                                        <span
                                                            className={`app-user-risk-band app-user-risk-band-${item.riskBand}`}
                                                            title="Based on the latest weekly overall score"
                                                        >
                                                            <span aria-hidden="true">
                                                                {item.riskBand ===
                                                                "green"
                                                                    ? "🟢"
                                                                    : item.riskBand ===
                                                                        "amber"
                                                                      ? "🟡"
                                                                      : "🔴"}
                                                            </span>
                                                            {
                                                                riskBandLabels[
                                                                    item.riskBand
                                                                ]
                                                            }
                                                            <small>
                                                                {item.weeklyOverallScore}
                                                            </small>
                                                        </span>
                                                    ) : (
                                                        <span className="app-user-risk-band app-user-risk-band-unknown">
                                                            No score
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={getOnboardingStatusBadge(
                                                            item.onboardingStatus,
                                                        )}
                                                    >
                                                        {formatOnboardingStatus(
                                                            item.onboardingStatus,
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={
                                                            item.currentWeekNumber
                                                                ? "app-user-week-pill"
                                                                : "app-user-week-pill app-user-week-pill-empty"
                                                        }
                                                    >
                                                        {item.currentWeekNumber
                                                            ? `Week ${item.currentWeekNumber}`
                                                            : "Not started"}
                                                    </span>
                                                </td>
                                                <td className="registration-path-column">
                                                    <span
                                                        className={getRegistrationMethodBadge(
                                                            item.registrationMethod,
                                                        )}
                                                        title="Method used to register the account"
                                                    >
                                                        {formatRegistrationMethod(
                                                            item.registrationMethod,
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="app-users-status-cell">
                                                        <span
                                                            className={getStatusBadge(
                                                                item.isLoginReleased
                                                                    ? "released"
                                                                    : item.status,
                                                            )}
                                                        >
                                                            {item.isLoginReleased
                                                                ? "released"
                                                                : item.status ||
                                                                  "pending"}
                                                        </span>
                                                        <Form.Check
                                                            type="switch"
                                                            checked={
                                                                item.status ===
                                                                "active"
                                                            }
                                                            disabled={
                                                                !canToggleStatus
                                                            }
                                                            onChange={() =>
                                                                AppUserApi.ChangeUserActiveDeactivate(
                                                                    user,
                                                                    String(
                                                                        displayUserId,
                                                                    ),
                                                                ).then(() =>
                                                                    fetchAppUserList(
                                                                        pagination.current_page,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                                <td className="app-users-actions">
                                                    {item.isJoined !== false && (
                                                            <Link
                                                                to={`/app-user/${displayUserId}/insights`}
                                                                className="app-user-insights-btn"
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faChartLine
                                                                    }
                                                                />
                                                                <span>
                                                                    Insights
                                                                </span>
                                                            </Link>
                                                        )}
                                                    <UserActionsDropdown
                                                        userId={displayUserId}
                                                        userName={displayName}
                                                        userEmail={item.email}
                                                        userPhone={item.phone}
                                                        patientProfileId={
                                                            item.patientProfileId ??
                                                            null
                                                        }
                                                        isJoined={
                                                            item.isJoined ??
                                                            true
                                                        }
                                                        fetchList={() =>
                                                            fetchAppUserList(
                                                                pagination.current_page,
                                                            )
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={10}
                                            className="text-center text-muted"
                                        >
                                            No Users Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                        <CustomPagination
                            total={pagination.total}
                            perPage={pagination.per_page}
                            currentPage={pagination.current_page}
                            changePage={(page: number) => {
                                navigate(`/app-user?current_page=${page}`);
                                fetchAppUserList(page);
                            }}
                        />
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};
